import { Image } from "expo-image";
import * as Linking from "expo-linking";
import { useTranslation } from "react-i18next";
import { Platform, Pressable, ScrollView, View } from "react-native";
import { SafeAreaView } from "react-native-safe-area-context";

import BasisIcon from "@/assets/images/basis_prague_logo.svg";
import { Icon } from "@/components/ui/icon";
import { Text } from "@/components/ui/text";
import {
    formatPhoneDisplay,
    localizedText,
    resolveContactAssetUrl,
    useContactInfo,
    type CampusContact,
    type ContactPerson,
} from "@/features/menu/contactInfo";
import { THEME } from "@/lib/theme";
import { useColorScheme } from "nativewind";

const CONTACT_LINK_ICON_SIZE = 14;
/** Matches `text-base` line-height so the icon optically sits on the first line. */
const CONTACT_LINK_LINE_HEIGHT = 24;

async function openUrl(url: string) {
    try {
        await Linking.openURL(url);
    } catch {
        // Ignore — device may not support the scheme.
    }
}

function openMaps(address: string) {
    const query = encodeURIComponent(address);
    const url =
        Platform.OS === "ios"
            ? `https://maps.apple.com/?q=${query}`
            : `https://www.google.com/maps/search/?api=1&query=${query}`;
    return openUrl(url);
}

function ContactLink({
    icon,
    label,
    onPress,
}: {
    icon: "phone" | "envelope" | "location-dot" | "globe";
    label: string;
    onPress: () => void;
}) {
    return (
        <Pressable
            accessibilityRole="link"
            onPress={onPress}
            className="flex-row items-start gap-2 active:opacity-70"
        >
            <View
                className="items-center justify-center"
                style={{
                    width: CONTACT_LINK_ICON_SIZE,
                    height: CONTACT_LINK_LINE_HEIGHT,
                }}
            >
                <Icon
                    name={icon}
                    iconStyle="solid"
                    size={CONTACT_LINK_ICON_SIZE}
                    className="text-secondary"
                />
            </View>
            <Text className="min-w-0 flex-1 shrink">{label}</Text>
        </Pressable>
    );
}

function PersonRow({ person }: { person: ContactPerson }) {
    const showName =
        person.name !== person.email &&
        !(person.phone && person.name === formatPhoneDisplay(person.phone));

    return (
        <View className="gap-1.5">
            {showName ? <Text variant="bold">{person.name}</Text> : null}
            {person.phone ? (
                <ContactLink
                    icon="phone"
                    label={formatPhoneDisplay(person.phone)}
                    onPress={() => openUrl(`tel:${person.phone}`)}
                />
            ) : null}
            {person.email ? (
                <ContactLink
                    icon="envelope"
                    label={person.email}
                    onPress={() => openUrl(`mailto:${person.email}`)}
                />
            ) : null}
        </View>
    );
}

function CampusCard({ campus }: { campus: CampusContact }) {
    const { t, i18n } = useTranslation();
    const campusName = localizedText(campus.name, i18n.language);
    const imageUri = campus.imageUrl
        ? resolveContactAssetUrl(campus.imageUrl)
        : undefined;

    return (
        <View className="overflow-hidden rounded-card border border-border bg-card shadow-subtle">
            {imageUri ? (
                <Image
                    source={{ uri: imageUri }}
                    style={{ width: "100%", height: 180 }}
                    contentFit="cover"
                    cachePolicy="disk"
                    accessibilityLabel={t("contact.campusAccessibilityLabel", {
                        name: campusName,
                    })}
                />
            ) : null}

            <View className="gap-4 p-4">
                <View className="gap-2">
                    <Text variant="h3" className="text-primary">
                        {campusName}
                    </Text>
                    {campus.officeHours ? (
                        <Text variant="muted">
                            {t("contact.officeHours", {
                                hours: campus.officeHours,
                            })}
                        </Text>
                    ) : null}
                    <ContactLink
                        icon="location-dot"
                        label={campus.address}
                        onPress={() => openMaps(campus.address)}
                    />
                    {campus.website ? (
                        <ContactLink
                            icon="globe"
                            label={campus.website.label || t("contact.website")}
                            onPress={() => openUrl(campus.website!.url)}
                        />
                    ) : null}
                </View>

                {campus.groups.map((group, groupIndex) => {
                    const groupTitle = localizedText(group.title, i18n.language);
                    return (
                        <View key={`${campus.id}-${groupTitle}-${groupIndex}`} className="gap-2">
                            <Text
                                variant="caption"
                                className="uppercase tracking-wide"
                            >
                                {groupTitle}
                            </Text>
                            <View className="gap-3">
                                {group.people.map((person) => (
                                    <PersonRow
                                        key={`${groupTitle}-${person.name}-${person.email ?? person.phone}`}
                                        person={person}
                                    />
                                ))}
                            </View>
                        </View>
                    );
                })}
            </View>
        </View>
    );
}

export default function ContactUsScreen() {
    const { t } = useTranslation();
    const { colorScheme } = useColorScheme();
    const scheme = colorScheme ?? "light";
    const info = useContactInfo();

    return (
        <SafeAreaView className="flex-1 bg-background" edges={["bottom"]}>
            <ScrollView
                className="flex-1"
                contentContainerClassName="gap-6 px-4 pb-6 pt-2 web:mx-auto web:w-full web:max-w-lg"
            >
                <View className="overflow-hidden rounded-card border border-border bg-card p-4 shadow-subtle">
                    <BasisIcon
                        style={{
                            width: 120,
                            height: 60,
                            marginLeft: "auto",
                            marginRight: "auto",
                            marginBottom: 20,
                            color: THEME[scheme].foreground,
                        }}
                        className="text-foreground"
                    />
                    <View className="gap-2">
                        <Text
                            variant="caption"
                            className="uppercase tracking-wide"
                        >
                            {t("contact.general")}
                        </Text>
                        <View className="gap-2">
                            <ContactLink
                                icon="envelope"
                                label={info.general.email}
                                onPress={() =>
                                    openUrl(`mailto:${info.general.email}`)
                                }
                            />
                            <ContactLink
                                icon="phone"
                                label={formatPhoneDisplay(info.general.phone)}
                                onPress={() =>
                                    openUrl(`tel:${info.general.phone}`)
                                }
                            />
                            {info.general.website ? (
                                <ContactLink
                                    icon="globe"
                                    label={info.general.website.label || t("contact.website")}
                                    onPress={() =>
                                        openUrl(info.general.website!.url)
                                    }
                                />
                            ) : null}
                        </View>
                    </View>
                </View>

                <Text variant="caption" className="uppercase tracking-wide">
                    {t("contact.ourCampuses")}
                </Text>
                {info.campuses.map((campus) => (
                    <CampusCard key={campus.id} campus={campus} />
                ))}
            </ScrollView>
        </SafeAreaView>
    );
}
